import * as React from "react";
import { useContext } from "react";
import { useDraggableContainer } from "../../hooks/useDraggableContainer";
import { useLoc } from "../../hooks/useLoc";
import {
    convertToFahrenheit,
    convertToInches
} from "../../services/convertUnits";
import firstCapitalCase from "../../services/firstCapitalCase";
import { measureColors } from "../../services/measureColors";
import { prepareNumber } from "../../services/prepareNumber";
import { WeatherData } from "../../templates/fragments/weather-data";
import { FadedOverlay } from "../shared/base/faded-ovelay";

import Cloudy from "images/ico_cloudy.svg";
import Drop from "images/ico_drop.svg";
import Lightbulb from "images/ico_lightbulb.svg";
import Moon from "images/ico_moon.svg";
import Rain from "images/ico_rainy.svg";
import SeaTemp from "images/ico_sea_temperature_red.svg";
import Sun from "images/ico_sun_red.svg";
import SunnyDays from "images/ico_sunny_days.svg";
import { TemperatureUnitContext } from "../../context/app-context";
import { useElementSize } from "../../hooks/useElementSize";
import { useScrollHandler } from "../../hooks/useScrollHandler";
import { ScrollButtons } from "../shared/base/scroll-buttons";

type StatCardsProps = {
    weatherData: WeatherData;
    monthIndex: number;
};

export const StatCards = ({ weatherData, monthIndex }: StatCardsProps) => {
    return (
        <>
            <DesktopWrapper className="mdmax:hidden">
                <Cards weatherData={weatherData} monthIndex={monthIndex} />
            </DesktopWrapper>
            <MobileWrapper className="md:hidden">
                <Cards weatherData={weatherData} monthIndex={monthIndex} />
            </MobileWrapper>
        </>
    );
};

const Cards = ({ weatherData, monthIndex }: StatCardsProps) => {
    const loc = useLoc();
    const [unit] = useContext(TemperatureUnitContext);
    return (
        <>
            <StatCard
                iconType={"temperature"}
                title={firstCapitalCase(loc("common.day"))}
                mainStat={prepareStat(
                    weatherData.tmax[monthIndex],
                    unit,
                    "degree"
                )}
                color={measureColors["tmax"]}
            />
            <StatCard
                iconType={"nightTemperature"}
                title={firstCapitalCase(loc("common.night"))}
                mainStat={prepareStat(
                    weatherData.tmin[monthIndex],
                    unit,
                    "degree"
                )}
                color={measureColors["tmin"]}
            />
            {weatherData.sst[monthIndex] != 0 && (
                <StatCard
                    iconType={"seaTemperature"}
                    title={firstCapitalCase(loc("common.sea"))}
                    mainStat={prepareStat(
                        weatherData.sst[monthIndex],
                        unit,
                        "degree"
                    )}
                    color={measureColors["sst"]}
                />
            )}
            <StatCard
                iconType={"rainyDays"}
                title={firstCapitalCase(loc("common.measures.ppt"))}
                mainStat={prepareStat(
                    weatherData.ppt[monthIndex],
                    unit,
                    "height"
                )}
                additionalInfo={loc("common.inAMonth")}
                color={measureColors["ppt"]}
            />
            <StatCard
                iconType={"rainyDays"}
                title={firstCapitalCase(loc("common.rainyDays"))}
                mainStat={`${prepareNumber(
                    weatherData.wet[monthIndex],
                    0
                )} ${loc("common.daysGenitive")}`}
                additionalInfo={loc("common.inAMonth")}
                color={measureColors["wet"]}
            />
            <StatCard
                iconType={"dayLight"}
                title={firstCapitalCase(loc("common.dayLight"))}
                mainStat={`${prepareNumber(
                    weatherData.dayhrs[monthIndex],
                    0
                )} ${loc("common.hoursGenitive")}`}
                additionalInfo={loc("common.avg")}
                color={measureColors["dayhrs"]}
            />
            {weatherData.sunhrs[monthIndex] != 0 && (
                <StatCard
                    iconType={"sunshine"}
                    title={firstCapitalCase(loc("common.sunshine"))}
                    mainStat={`${prepareNumber(
                        weatherData.sunhrs[monthIndex],
                        0
                    )} ${loc("common.hoursGenitive")}`}
                    additionalInfo={loc("common.avg")}
                    color={measureColors["sunhrs"]}
                />
            )}
            {weatherData.rhm[monthIndex] != 0 && (
                <StatCard
                    iconType={"humidity"}
                    title={firstCapitalCase(loc("common.humidity"))}
                    mainStat={`${prepareNumber(
                        weatherData.rhm[monthIndex],
                        0
                    )} %`}
                    color={measureColors["rhm"]}
                />
            )}
        </>
    );
};

const DesktopWrapper = ({
    children,
    className
}: {
    children: React.ReactNode;
    className?: string;
}) => {
    const scrollRef = React.useRef<null | any>(null);
    const { needEdgeOverlay, isDragging } = useDraggableContainer(scrollRef);
    const firstCard = (scrollRef?.current?.children || [])[0];
    const scrollDiff = useElementSize({ el: firstCard });
    const width = useElementSize({});
    const scrollHandler = useScrollHandler({ ref: scrollRef });
    const onScroll = ({ isLeft }: { isLeft: boolean }) => {
        scrollHandler(
            (scrollDiff + 8) * (isLeft ? -1 : 1) * (width > 400 ? 2 : 1)
        );
    };

    return (
        <div
            className={`relative mx-auto h-[196px] w-11/12 overflow-x-hidden ${className}`}
        >
            <div className={`absolute left-0 right-0 h-full`}>
                <div
                    ref={scrollRef}
                    className={`h-full overflow-x-scroll whitespace-nowrap text-center scrollbar-none ${
                        isDragging ? "scroll-auto" : "scroll-smooth"
                    }`}
                >
                    {children}
                </div>
                <FadedOverlay
                    fadeLeft={needEdgeOverlay.left}
                    fadeRight={needEdgeOverlay.right}
                    isBigger={true}
                ></FadedOverlay>
                {(needEdgeOverlay.left || needEdgeOverlay.right) && (
                    <ScrollButtons
                        fadeLeft={needEdgeOverlay.left}
                        fadeRight={needEdgeOverlay.right}
                        onClick={onScroll}
                    />
                )}
            </div>
        </div>
    );
};

const MobileWrapper = ({
    children,
    className
}: {
    children: React.ReactNode;
    className?: string;
}) => {
    return (
        <div
            className={`mx-auto grid w-11/12 grid-cols-2 gap-y-[16px] xm:grid-cols-3 sm:grid-cols-4 ${className}`}
        >
            {children}
        </div>
    );
};

type StatCardProps = {
    iconType:
        | "temperature"
        | "seaTemperature"
        | "nightTemperature"
        | "rainyDays"
        | "sunnyDays"
        | "dayLight"
        | "sunshine"
        | "humidity";
    title: string;
    color?: string;
    mainStat: string;
    additionalInfo?: string;
};
const StatCard = ({
    iconType,
    title,
    mainStat,
    color,
    additionalInfo
}: StatCardProps) => {
    const iconSources = {
        temperature: Cloudy,
        seaTemperature: SeaTemp,
        nightTemperature: Moon,
        rainyDays: Rain,
        sunnyDays: SunnyDays,
        dayLight: Lightbulb,
        sunshine: Sun,
        humidity: Drop
    };
    return (
        <>
            <div className="mx-[8px] inline-flex h-full min-w-[130px] flex-col items-center rounded-sm border-[1px] text-center mdmax:h-[196px]">
                <div
                    style={{ backgroundColor: color }}
                    className="mb-[32px] h-[4px] w-full "
                >
                    &nbsp;
                </div>
                <img
                    src={iconSources[iconType]}
                    alt=""
                    className="mb-[20px] h-[32px]"
                />
                <p className="text-sm leading-[18px] tracking-wide">{title}</p>
                <p className="my-[12px] text-2xl font-bold leading-[20px] tracking-wide">
                    {mainStat}
                </p>
                <p className="text-sm leading-[18px] tracking-wide text-zinc-500">
                    {additionalInfo}
                </p>
            </div>
        </>
    );
};

const prepareStat = (val, unit, type?: "degree" | "height") => {
    if (type == "degree") {
        val = unit == "F" ? convertToFahrenheit(val) : val;
        return prepareNumber(val, 0) + ` °${unit}`;
    }
    if (type == "height") {
        val = unit == "F" ? convertToInches(val) : val;
        val = unit == "F" ? prepareNumber(val, 2) : prepareNumber(val, 0);
        return val + ` ${unit == "C" ? "mm" : "in"}`;
    }
};
